import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_643f3f34-a1d4-4c71-8acb-3c1d3f8beb8d/artifacts/bl51k3pb_95533D89-11D2-45C0-9D79-4F0FFEBEC907.png";

const navLinks = [
  { to: "/packages", label: "Packages" },
  { to: "/services", label: "Services" },
  { to: "/growth-plan", label: "Growth Plan" },
  { to: "/pricing", label: "Pricing" },
  { to: "/work", label: "Work" },
  { to: "/resources", label: "Resources" },
  { to: "/faq", label: "FAQ" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a0a0a]/90 backdrop-blur-md border-b border-[#27272a]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="navbar-logo-link"
            className="flex items-center gap-3"
            aria-label="Agent Marketing home"
          >
            <img
              src={LOGO_URL}
              alt="Agent Marketing"
              className="h-10 w-10 object-contain"
              width="40"
              height="40"
            />
            <div className="hidden sm:block leading-tight">
              <div className="font-heading text-[15px] tracking-widest text-white uppercase">
                Agent<span className="text-[#C8A24B] ml-1">Marketing</span>
              </div>
              <div className="text-[9px] tracking-[0.28em] text-[#71717a] uppercase mt-0.5">
                Where Clients Find You.
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-[13px] font-medium tracking-wide transition-colors ${
                    isActive
                      ? "text-[#C8A24B]"
                      : "text-[#a1a1aa] hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="navbar-cta-button"
              className="btn-gold text-[13px] py-3 px-5"
            >
              Build My Growth Plan
            </Link>
          </div>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[#0a0a0a] border-t border-[#27272a]"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 border-b border-[#171717] text-[15px] font-medium ${
                    isActive ? "text-[#C8A24B]" : "text-[#d4d4d8]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta"
              className="btn-gold mt-6 w-full"
            >
              Build My Growth Plan
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
